import { Button } from "@/components/ui/button";
import React from "react";

interface StaffInfoPanelProps {
    staff: any;
    rightPanelTab: "basicInfo" | "employeeIdentifiers";
    setRightPanelTab: (tab: "basicInfo" | "employeeIdentifiers") => void;
}

const StaffInfoPanel: React.FC<StaffInfoPanelProps> = ({ staff, rightPanelTab, setRightPanelTab }) => {
    return (
        <div className="lg:col-span-1 border-l">
            {/* Tab Headers */}
            <div className="flex flex-row gap-x-2 p-4 border-b">
                <Button 
                    variant={rightPanelTab === "basicInfo" ? "outline" : "ghost"}
                    onClick={() => setRightPanelTab("basicInfo")}
                    className="gap-x-2 font-normal"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Basic Info
                </Button>
                <Button 
                    variant={rightPanelTab === "employeeIdentifiers" ? "outline" : "ghost"}
                    onClick={() => setRightPanelTab("employeeIdentifiers")}
                    className="gap-x-2 font-normal"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                    </svg>
                    Employee Identifiers
                </Button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
                {/* Basic Info */}
                {rightPanelTab === "basicInfo" && (
                    <div className="space-y-3">
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">EMPLOYEE ID</span>
                            <span className="text-sm font-medium">{staff.employeeId}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">DEPARTMENT</span>
                            <span className="text-sm">{staff.department}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">JOB TITLE</span>
                            <span className="text-sm">{staff.jobTitle}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">WORK LOCATION</span>
                            <span className="text-sm">{staff.workLocation}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">JOINING DATE</span>
                            <span className="text-sm">{staff.joiningDate}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">REPORTING MANAGER</span>
                            <span className="text-sm">{staff.reportingManager}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">EMPLOYMENT TYPE</span>
                            <span className="text-sm text-blue-600">{staff.employmentType}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">SHIFT</span>
                            <span className="text-sm">{staff.shift}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">STATUS</span>
                            <span className="text-sm">{staff.status}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">PERFORMANCE</span>
                            <span className="text-sm">{staff.performance}</span>
                        </div>
                    </div>
                )}

                {/* Employee Identifiers */}
                {rightPanelTab === "employeeIdentifiers" && (
                    <div className="space-y-3">
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">EMPLOYEE NUMBER</span>
                            <span className="text-sm font-medium">{staff.employeeId}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">NATIONAL ID</span>
                            <span className="text-sm">784-{Math.random().toString().slice(2, 13)}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">PASSPORT NUMBER</span>
                            <span className="text-sm">P{Math.random().toString().slice(2, 10)}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">TAX ID</span>
                            <span className="text-sm">TRN-{Math.random().toString().slice(2, 11)}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">SOCIAL SECURITY</span>
                            <span className="text-sm">SSN-{Math.random().toString().slice(2, 11)}</span>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <span className="text-sm text-gray-600">LICENSE NUMBER</span>
                            <span className="text-sm">DL-{Math.random().toString().slice(2, 11)}</span>
                        </div>
                    </div>
                )}

                {/* HR Notes */}
                <div>
                    <span className="text-base font-medium mb-3 block">HR Notes</span>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <span className="text-sm">{staff.hrNotes}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffInfoPanel;
